/**
 * Single rendered scope bar from {@link buildTimelineBars}: label, draggable
 * body (whole bar except resize edges), and narrow start/end handles for span resize.
 * Per-day strips use CSS :hover for column highlight. Short click opens a pinned scope info
 * popover above the bar; ⌘/Ctrl + short click toggles persisted `skipped` on that day’s aspect.
 * Crew edits run from actions inside that popover.
 * Bars whose block span includes a dispatch-locked day (`block.contains_locked`) cannot be dragged.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { DEBUG_SHOW_DISPATCH_EDITED, PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import { buildScheduleBarDragId } from "../dnd/scheduleDndIds";
import { SCHEDULE_BAR } from "../constants";
import { SCHEDULE_BAR_STYLE } from "../style";
import { toLocalDateKey } from "../../../../utils/datetime/core";
import { getScheduleAspectForBlockOnDate } from "../model/scheduleBlockSelectors";
import { countSelectedWorkersOnScheduleBlockDay } from "../model/scheduleFilterModel";
import { getOverrideRuns } from "../model/scheduleOverrideStripRuns";
import { resolveScopeBlockColorAssignment } from "../model/scheduleWorkerAssignmentDiff";
import OverrideStrip from "./OverrideStrip";
import { useScopeCardPopover } from "./ScopeCard";
import {
  firstForemanFromAssignment,
  getWorkerRoleColorClass,
} from "../../../../utils/business/worker";
import { CascadeDropDown } from "../../../../components/ui";
import { useScheduleContext } from "../../../../context/domain/planning/ScheduleContext";
import { SKIPPED_DAY_HATCH_STYLE } from "../../shared/SkippedDayOverlay";
import ScopeBarWorkerInitialsRow from "./ScopeBarWorkerInitialsRow";

/** Treat as click only if movement stays within DnD activation distance (see {@link ScheduleTimelineDnd}). */
const SHORT_CLICK_MAX_MOVE_SQ = 7 * 7;

/** Hatched overlay (teal-600) marking days where one or more filtered workers are assigned. */
const SELECTED_WORKER_HATCH_STYLE = {
  backgroundImage: `repeating-linear-gradient(-45deg, #0d9488 0 2px, transparent 2px 4px)`,
};

/**
 * Render one scope bar segment: label, draggable body, resize handles, override strips.
 * Crew and skip actions are read from {@link useScheduleContext}.
 *
 * @param {Object} props
 * @param {Object} props.bar Render model from {@link buildTimelineBars} (`id`, geometry, label).
 * @param {Object} props.block WorkScopeAspectsBlock-shaped block for this bar.
 * @param {Map<string|number, Object>} props.workScopeById Catalog lookup for labels.
 * @param {number} props.cellWidth Day column width in pixels.
 * @param {Array<Date>} [props.visibleDays] Visible timeline days (resize column snapping).
 * @param {(edge: 'start'|'end', event: PointerEvent, block: Object) => void} props.beginResize
 * @param {(event: PointerEvent) => void} props.onResizePointerMove
 * @param {(event: PointerEvent) => void} props.onResizePointerUp
 * @param {(event: PointerEvent) => void} props.onResizePointerCancel
 * @returns {JSX.Element}
 */
export default function ScopeBar({
  bar: scopeBar,
  block: scopeBlock,
  workScopeById,
  cellWidth,
  visibleDays = [],
  beginResize,
  onResizePointerMove,
  onResizePointerUp,
  onResizePointerCancel,
}) {
  const {
    workScopeAspects = [],
    handleBarDayShortClick,
    handleRemoveDayCrewOverride,
    handleCommitOverrideStripRange,
    handleToggleAspectSkipped,
    scheduleCrewClipboard,
    copyScheduleCrew,
    pasteScheduleCrew,
    requestDeleteScopeBlock,
    openScopeSettings,
    copyScopeBlock,
    handleSkippedReasonChange,
    isScheduleDayLocked,
    filterWorkerId,
    scheduleViewMode,
    readOnly,
  } = useScheduleContext();
  const dragId = buildScheduleBarDragId(scopeBar.id);
  /** Whole-bar drag is blocked when the merged block span touches any locked dispatch day. */
  const dragPreventedDueToLockedDays = Boolean(scopeBlock?.contains_locked);
  const dragDisabled = readOnly || dragPreventedDueToLockedDays;
  const startDateKey = scopeBlock?.startDate
    ? toLocalDateKey(scopeBlock.startDate)
    : null;
  const endDateKey = scopeBlock?.endDate ? toLocalDateKey(scopeBlock.endDate) : null;
  const startResizeLocked = Boolean(startDateKey && isScheduleDayLocked?.(startDateKey));
  const endResizeLocked = Boolean(endDateKey && isScheduleDayLocked?.(endDateKey));
  const dragDataRef = useRef({
    type: "schedule-bar",
    blockId: scopeBar.blockId,
    barId: scopeBar.id,
    grabDayKey: null,
  });
  dragDataRef.current.blockId = scopeBar.blockId;
  dragDataRef.current.barId = scopeBar.id;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: dragDataRef.current,
    disabled: dragDisabled,
  });

  /**
   * Crew used for bar color: uniform per-day override if every day matches, else block default.
   *
   * @param {void}
   * @returns {Array<Object>}
   */
  const colorAssignment = useMemo(
    () => resolveScopeBlockColorAssignment(scopeBlock, workScopeAspects),
    [scopeBlock, workScopeAspects]
  );
  /**
   * First foreman in the color assignment, used for the bar surface role color.
   *
   * @param {void}
   * @returns {Object|null|undefined}
   */
  const foreman = useMemo(
    () => firstForemanFromAssignment(colorAssignment),
    [colorAssignment]
  );
  /**
   * Tailwind role-color class for the bar surface (empty when no foreman).
   *
   * @param {void}
   * @returns {string}
   */
  const foremanColor = useMemo(() => getWorkerRoleColorClass(foreman), [foreman]);

  const hoverAnchorRef = useRef(null);
  const contextMenuBlockIdRef = useRef(null);
  const barContextOpenRef = useRef(false);
  const [barContextOpen, setBarContextOpen] = useState(false);
  const [barContextAnchor, setBarContextAnchor] = useState(null);
  const [workerInitialsExpanded, setWorkerInitialsExpanded] = useState(false);

  useEffect(() => {
    barContextOpenRef.current = barContextOpen;
  }, [barContextOpen]);

  /**
   * Dismiss the bar context menu and clear its anchor.
   *
   * @param {void}
   * @returns {void}
   */
  const closeBarContextMenu = useCallback(() => {
    setBarContextOpen(false);
    setBarContextAnchor(null);
    contextMenuBlockIdRef.current = null;
  }, []);

  /**
   * Open (or toggle closed) the Copy/Delete menu; read geometry now because currentTarget clears later.
   *
   * @param {import('react').MouseEvent} e Context-menu event.
   * @returns {void}
   */
  const onBarContextMenu = useCallback(
    (e) => {
      if (!scopeBlock) {
        return;
      }
      if (readOnly) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      // Read geometry synchronously — `e.currentTarget` is cleared before functional setState runs.
      const anchorRect = e.currentTarget?.getBoundingClientRect?.() ?? null;
      if (!anchorRect) {
        return;
      }

      const toggleClose =
        barContextOpenRef.current &&
        contextMenuBlockIdRef.current === scopeBar.blockId;
      if (toggleClose) {
        contextMenuBlockIdRef.current = null;
        setBarContextAnchor(null);
        setBarContextOpen(false);
        return;
      }

      contextMenuBlockIdRef.current = scopeBar.blockId;
      setBarContextAnchor(anchorRect);
      setBarContextOpen(true);
    },
    [readOnly, scopeBar.blockId, scopeBlock]
  );

  /**
   * Copy/Delete actions for the bar context menu.
   *
   * @param {void}
   * @returns {Array<{ id: string, label: string, onSelect: Function }>}
   */
  const barContextItems = useMemo(
    () => [
      {
        id: "copy",
        label: "Copy",
        onSelect: () => {
          if (scopeBlock) {
            copyScopeBlock(scopeBlock);
          }
        },
      },
      {
        id: "delete",
        label: "Delete",
        onSelect: () => {
          requestDeleteScopeBlock(scopeBlock);
        },
      },
    ],
    [scopeBlock, requestDeleteScopeBlock, copyScopeBlock]
  );

  /**
   * Attach both the dnd-kit node and the ScopeCard popover anchor to the bar root.
   *
   * @param {HTMLElement|null} node Bar element.
   * @returns {void}
   */
  const setBarNodeRef = useCallback(
    (node) => {
      setNodeRef(node);
      hoverAnchorRef.current = node;
    },
    [setNodeRef]
  );

  const firstVis =
    scopeBar.firstVisibleIdx != null
      ? scopeBar.firstVisibleIdx
      : Math.round(
        (scopeBar.left - SCHEDULE_BAR.leftOffset) / Math.max(cellWidth, 1)
      );
  const lastVis =
    scopeBar.lastVisibleIdx != null
      ? scopeBar.lastVisibleIdx
      : Math.max(
        firstVis,
        firstVis +
        Math.max(
          0,
          Math.round(
            (scopeBar.width + SCHEDULE_BAR.widthShrink) / Math.max(cellWidth, 1)
          ) - 1
        )
      );

  /**
   * Map a pointer x to a visible timeline day via bar-left + cellWidth (not week-start aligned).
   *
   * @param {number} clientX Pointer x in viewport pixels.
   * @param {HTMLElement} barEl Bar root used for getBoundingClientRect.
   * @returns {Date|null}
   */
  const resolveDayFromClientX = useCallback(
    (clientX, barEl) => {
      if (!visibleDays.length || cellWidth <= 0 || !barEl) {
        return null;
      }
      const rect = barEl.getBoundingClientRect();
      const xInBar = clientX - rect.left;
      const dayOffset = Math.floor(xInBar / cellWidth);
      const dayIdx = Math.max(
        0,
        Math.min(visibleDays.length - 1, firstVis + dayOffset)
      );
      return visibleDays[dayIdx] ?? null;
    },
    [cellWidth, firstVis, visibleDays]
  );

  const colCount = lastVis >= firstVis ? lastVis - firstVis + 1 : 0;
  /**
   * `YYYY-MM-DD` → visibleDays index, used to pin the popover to a clicked column.
   *
   * @param {void}
   * @returns {Map<string, number>}
   */
  const visibleDayIndexByKey = useMemo(() => {
    const idxByKey = new Map();
    for (let i = 0; i < visibleDays.length; i += 1) {
      idxByKey.set(toLocalDateKey(visibleDays[i]), i);
    }
    return idxByKey;
  }, [visibleDays]);

  /**
   * Date keys of dispatch-edited aspects on this bar (debug hatch only).
   *
   * @param {void}
   * @returns {Set<string>}
   */
  const dispatchEditedDayKeys = useMemo(() => {
    if (!DEBUG_SHOW_DISPATCH_EDITED) {
      return new Set();
    }
    const keys = new Set();
    for (const aspect of workScopeAspects) {
      if (
        aspect?.workScopeAspectsBlock?.id === scopeBar.blockId &&
        aspect?.dispatch_modified &&
        aspect?.date != null
      ) {
        keys.add(toLocalDateKey(aspect.date));
      }
    }
    return keys;
  }, [workScopeAspects, scopeBar.blockId]);

  /**
   * Map pointer x to a 0-based column within this bar strip for override-handle resize.
   *
   * @param {number} clientX Pointer x in viewport pixels.
   * @returns {number|null}
   */
  const resolveRelColumnFromClientX = useCallback(
    (clientX) => {
      const barEl = hoverAnchorRef.current;
      if (!visibleDays.length || cellWidth <= 0 || !barEl || colCount <= 0) {
        return null;
      }
      const rect = barEl.getBoundingClientRect();
      const xInBar = clientX - rect.left;
      const dayOffset = Math.floor(xInBar / cellWidth);
      return Math.max(0, Math.min(colCount - 1, dayOffset));
    },
    [cellWidth, colCount, visibleDays.length]
  );

  /**
   * Center the pinned ScopeCard on the clicked day column, using leftover width on the last cell.
   *
   * @param {Date} day Clicked calendar day.
   * @param {DOMRect} rect Bar getBoundingClientRect.
   * @returns {number}
   */
  const resolvePopoverLeftForDay = useCallback(
    (day, rect) => {
      if (!(day instanceof Date) || colCount <= 0) {
        return rect.left + rect.width / 2;
      }
      const visibleIdx = visibleDayIndexByKey.get(toLocalDateKey(day));
      if (visibleIdx == null) {
        return rect.left + rect.width / 2;
      }
      const relCol = Math.max(0, Math.min(colCount - 1, visibleIdx - firstVis));
      const isLast = relCol === colCount - 1;
      const colWidth = isLast
        ? Math.max(0, scopeBar.width - relCol * cellWidth)
        : cellWidth;
      return rect.left + relCol * cellWidth + colWidth / 2;
    },
    [scopeBar.width, cellWidth, colCount, firstVis, visibleDayIndexByKey]
  );

  /**
   * Contiguous per-day crew-override runs rendered as orange strips.
   *
   * @param {void}
   * @returns {Array<Object>}
   */
  const overrideRuns = useMemo(
    () => getOverrideRuns(scopeBar.perDayWorkerOverride),
    [scopeBar.perDayWorkerOverride]
  );

  /**
   * Open the per-day crew editor for the clicked bar day.
   *
   * @param {Date} day Calendar day.
   * @returns {void}
   */
  const onOpenDayCrewModal = useCallback(
    (day) => {
      if (!day) {
        return;
      }
      handleBarDayShortClick(scopeBar.blockId, day);
    },
    [scopeBar.blockId, handleBarDayShortClick]
  );

  /**
   * Revert one day's crew override back to the block default.
   *
   * @param {Date} day Calendar day.
   * @returns {void}
   */
  const onRemoveDayCrewOverrideForBar = useCallback(
    (day) => {
      if (!day) {
        return;
      }
      handleRemoveDayCrewOverride(scopeBar.blockId, day);
    },
    [scopeBar.blockId, handleRemoveDayCrewOverride]
  );

  const barSurfaceBackgroundClassName = foremanColor || undefined;
  const barSurfaceBackgroundColor =
    !foremanColor && scopeBar.color?.trim() ? scopeBar.color.trim() : undefined;

  const { popoverPortal, openScopeCard, pinnedDayKey } = useScopeCardPopover(hoverAnchorRef, {
    block: scopeBlock,
    workScopeById,
    workScopeAspects,
    isDragging,
    resolveAnchorLeft: resolvePopoverLeftForDay,
    onOpenDayCrewModal,
    onRemoveDayCrewOverride: onRemoveDayCrewOverrideForBar,
    scheduleCrewClipboard,
    onCopyScheduleCrew: copyScheduleCrew,
    onPasteScheduleCrew: pasteScheduleCrew,
    barSurfaceBackgroundClassName,
    barSurfaceBackgroundColor,
    onOpenScopeSettings: openScopeSettings,
    onSkippedReasonChange: handleSkippedReasonChange,
    isScheduleDayLocked,
  });

  const pointerSessionRef = useRef(null);
  const { onPointerDown: listenerPointerDown, ...restListeners } =
    listeners ?? {};

  /**
   * Record grab-day + origin so pointerup can treat small movement as a click, not a drag.
   *
   * @param {import('react').PointerEvent} e Bar pointerdown.
   * @returns {void}
   */
  const onPointerDown = useCallback(
    (e) => {
      if (e.button === 2) {
        return;
      }
      if (
        e.target.closest("[data-schedule-resize-handle]") ||
        e.target.closest("[data-schedule-override-resize-handle]")
      ) {
        return;
      }
      const grabDay = resolveDayFromClientX(e.clientX, e.currentTarget);
      dragDataRef.current.grabDayKey = grabDay ? toLocalDateKey(grabDay) : null;
      pointerSessionRef.current = {
        pointerId: e.pointerId,
        x: e.clientX,
        y: e.clientY,
      };
      if (!dragDisabled) {
        listenerPointerDown?.(e);
      }
    },
    [dragDisabled, listenerPointerDown, resolveDayFromClientX]
  );

  /**
   * Short-click opens the ScopeCard; ⌘/Ctrl+click toggles skipped. Ignores resize-handle hits.
   *
   * @param {import('react').PointerEvent} e Bar pointerup.
   * @returns {void}
   */
  const onPointerUp = useCallback(
    (e) => {
      const sess = pointerSessionRef.current;
      pointerSessionRef.current = null;
      if (!sess || e.pointerId !== sess.pointerId) {
        return;
      }
      if (e.button === 2) {
        return;
      }
      if (
        e.target.closest("[data-schedule-resize-handle]") ||
        e.target.closest("[data-schedule-override-resize-handle]")
      ) {
        return;
      }
      if (isDragging) {
        return;
      }
      if (readOnly) {
        return;
      }
      const dx = e.clientX - sess.x;
      const dy = e.clientY - sess.y;
      if (dx * dx + dy * dy > SHORT_CLICK_MAX_MOVE_SQ) {
        return;
      }
      const day = resolveDayFromClientX(e.clientX, e.currentTarget);
      if (!day) {
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        handleToggleAspectSkipped(scopeBar.blockId, day);
        return;
      }
      openScopeCard(day);
    },
    [
      scopeBar.blockId,
      isDragging,
      handleToggleAspectSkipped,
      openScopeCard,
      readOnly,
      resolveDayFromClientX,
    ]
  );

  /**
   * Drop the pending click session when the pointer is cancelled mid-gesture.
   *
   * @param {void}
   * @returns {void}
   */
  const onPointerCancel = useCallback(() => {
    pointerSessionRef.current = null;
  }, []);

  const overridePresent = overrideRuns.length > 0;
  const clipInsetPx = SCHEDULE_BAR.leftOffset;
  const displayLeft = scopeBar.clippedStart
    ? Math.max(0, scopeBar.left - clipInsetPx)
    : scopeBar.left;
  const displayWidth = Math.max(
    SCHEDULE_BAR.minWidth,
    scopeBar.width +
    (scopeBar.clippedStart ? clipInsetPx : 0) +
    (scopeBar.clippedEnd ? clipInsetPx : 0)
  );
  const barBorderRadiusStyle = {
    borderTopLeftRadius: scopeBar.clippedStart ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
    borderBottomLeftRadius: scopeBar.clippedStart ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
    borderTopRightRadius: scopeBar.clippedEnd ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
    borderBottomRightRadius: scopeBar.clippedEnd ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
  };

  return (
    <>
      <div
        ref={setBarNodeRef}
        className={`absolute flex outline-none focus:outline-none focus-visible:outline-none ${barSurfaceBackgroundClassName ?? ""} ${dragDisabled ? "" : "hover:cursor-pointer cursor-grab active:cursor-grabbing"} items-center ${scopeBar.clippedStart ? "pl-0" : "pl-1"} ${scopeBar.clippedEnd ? "pr-0" : "pr-1"}`}
        style={{
          left: displayLeft,
          top: scopeBar.top,
          width: displayWidth,
          height: scopeBar.height,
          zIndex: workerInitialsExpanded
            ? SCHEDULE_BAR_STYLE.zIndex + 100
            : SCHEDULE_BAR_STYLE.zIndex,
          color: SCHEDULE_BAR_STYLE.textColor,
          fontSize: SCHEDULE_BAR_STYLE.fontSizePx,
          fontWeight: SCHEDULE_BAR_STYLE.fontWeight,
          boxShadow: SCHEDULE_BAR_STYLE.boxShadow,
          opacity: isDragging ? 0.35 : 1,
          touchAction: "none",
          backgroundColor: barSurfaceBackgroundColor,
          ...barBorderRadiusStyle,
        }}
        {...attributes}
        {...restListeners}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onContextMenu={onBarContextMenu}
      >
        {overridePresent ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-black/5"
            style={{
              borderTopLeftRadius: scopeBar.clippedStart ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
              borderTopRightRadius: scopeBar.clippedEnd ? 0 : SCHEDULE_BAR_STYLE.borderRadiusPx,
              marginLeft: scopeBar.clippedStart ? "0px" : "8px",
              marginRight: scopeBar.clippedEnd ? "0px" : "8px",
            }}
          />
        ) : null}
        {cellWidth > 0 && lastVis >= firstVis ? (
          <div
            className={`absolute inset-0 z-10 flex ${scopeBar.clippedStart && scopeBar.clippedEnd ? "rounded-none" : scopeBar.clippedStart ? "rounded-r-sm" : scopeBar.clippedEnd ? "rounded-l-sm" : "rounded-sm"}`}
            aria-hidden
          >
            {Array.from({ length: lastVis - firstVis + 1 }, (_, i) => {
              const n = lastVis - firstVis + 1;
              const isLast = i === n - 1;
              const w = isLast
                ? Math.max(0, scopeBar.width - i * cellWidth)
                : cellWidth;
              const day = visibleDays[firstVis + i];
              const key = day ? toLocalDateKey(day) : `col-${firstVis + i}`;
              const noWorkDay = Boolean(scopeBar.perDaySkipped?.[i]);
              const selectedWorkerCount =
                countSelectedWorkersOnScheduleBlockDay(
                  scopeBlock,
                  workScopeAspects,
                  day,
                  filterWorkerId
                );
              const isDispatchEditedDay =
                Boolean(day) && dispatchEditedDayKeys.has(toLocalDateKey(day));
              const isPinnedDayColumn =
                Boolean(day) &&
                pinnedDayKey != null &&
                pinnedDayKey === toLocalDateKey(day);
              return (
                <div
                  key={key}
                  className="group/sched-day-col relative h-full shrink-0"
                  style={{ width: w }}
                  title={
                    dragDisabled
                      ? PLANNING_TOOLTIP_MESSAGES.scopeBarDragDisabledInstructions
                      : PLANNING_TOOLTIP_MESSAGES.scopeBarDragInstructions
                  }
                >
                  {selectedWorkerCount > 0 ? (
                    <div
                      aria-hidden
                      className="opacity-60 pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
                      style={SELECTED_WORKER_HATCH_STYLE}
                    >
                      {selectedWorkerCount > 1 ? (
                        <span className="rounded bg-teal-700 px-1.5 text-xs font-semibold leading-tight text-white shadow-sm">
                          {selectedWorkerCount}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {noWorkDay ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-[2]"
                      style={SKIPPED_DAY_HATCH_STYLE}
                    />
                  ) : null}
                  {isDispatchEditedDay ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-[3] bg-black/20"
                    />
                  ) : null}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-[4] group-hover/sched-day-col:bg-white/30"
                  />
                  {isPinnedDayColumn ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-[5] bg-black/10"
                    />
                  ) : null}
                </div>
              );
            })}
            {!readOnly && handleCommitOverrideStripRange
              ? overrideRuns.map((run, ri) => {
                // `scopeBar.clippedStart/End` describe whether the *block span* continues outside the viewport.
                // Override strips additionally need edge clipping relative to the *visible bar columns*:
                // a pasted single-day override can begin at `run.startCol === 0` even when `scopeBar.clippedStart`
                // is true (true block start is off-screen), and we still need the left handle in that case.
                const overrideStripClippedStart =
                  Boolean(scopeBar.clippedStart) && run.startCol === 0;
                const overrideStripClippedEnd =
                  Boolean(scopeBar.clippedEnd) && run.endCol === colCount - 1;
                const overrideStartDay = visibleDays[firstVis + run.startCol];
                const overrideEndDay = visibleDays[firstVis + run.endCol];
                const overrideStartDayKey = overrideStartDay
                  ? toLocalDateKey(overrideStartDay)
                  : null;
                const overrideEndDayKey = overrideEndDay
                  ? toLocalDateKey(overrideEndDay)
                  : null;
                const overrideStartResizeLocked = Boolean(
                  overrideStartDayKey && isScheduleDayLocked?.(overrideStartDayKey)
                );
                const overrideEndResizeLocked = Boolean(
                  overrideEndDayKey && isScheduleDayLocked?.(overrideEndDayKey)
                );

                const seedDay = visibleDays[firstVis + run.startCol];
                const seedKey = seedDay ? toLocalDateKey(seedDay) : null;
                const seedAspect = seedKey
                  ? getScheduleAspectForBlockOnDate(
                    workScopeAspects,
                    scopeBar.blockId,
                    seedDay
                  )
                  : null;
                const overrideWorkerIds = (
                  seedAspect?.worker_assignment ?? []
                ).map((w) => w.id);
                return (
                  <OverrideStrip
                    key={`ov-${scopeBar.id}-${ri}`}
                    cellWidth={cellWidth}
                    barWidth={scopeBar.width}
                    firstVis={firstVis}
                    colCount={colCount}
                    visibleDays={visibleDays}
                    run={run}
                    overrideWorkerIds={overrideWorkerIds}
                    blockId={scopeBar.blockId}
                    clippedStart={overrideStripClippedStart}
                    clippedEnd={overrideStripClippedEnd}
                    startHandleLocked={overrideStartResizeLocked}
                    endHandleLocked={overrideEndResizeLocked}
                    onCommit={handleCommitOverrideStripRange}
                    resolveRelColumnFromClientX={resolveRelColumnFromClientX}
                  />
                );
              })
              : null}
            <div
              className="pointer-events-none absolute inset-0 z-[45] flex"
              aria-hidden
            >
              {Array.from({ length: lastVis - firstVis + 1 }, (_, i) => {
                const n = lastVis - firstVis + 1;
                const isLast = i === n - 1;
                const w = isLast
                  ? Math.max(0, scopeBar.width - i * cellWidth)
                  : cellWidth;
                const day = visibleDays[firstVis + i];
                const dk = day ? toLocalDateKey(day) : null;
                return (
                  <div
                    key={`sched-lock-col-${scopeBar.id}-${dk ?? i}`}
                    className="relative h-full shrink-0"
                    style={{ width: w }}
                  >
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        {!readOnly && scopeBar.resizeStart && !scopeBar.clippedStart ? (
          startResizeLocked ? (
            <div
              title={PLANNING_TOOLTIP_MESSAGES.draggingDisabledDueToLockedDay}
              aria-label={PLANNING_TOOLTIP_MESSAGES.draggingDisabledDueToLockedDay}
              className="absolute cursor-not-allowed inset-y-0 left-0 z-30 w-2 shrink-0 rounded-l border-0 bg-black/10 opacity-60"
            />
          ) : (
            <button
              type="button"
              data-schedule-resize-handle
              aria-label="Resize block start date"
              className="absolute inset-y-0 left-0 z-20 w-2 shrink-0 cursor-ew-resize touch-none rounded-l border-0 bg-black/10 hover:bg-black/15"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => beginResize(e, scopeBar.blockId, "start")}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
              onPointerCancel={onResizePointerCancel}
            />
          )
        ) : null}
        <span
          className={`relative ${workerInitialsExpanded ? "z-[70]" : "z-20"} flex min-w-0 flex-1 items-center gap-1 px-1 ${scopeBar.clippedStart ? "pl-1" : "pl-2"} text-[11px] text-black pointer-events-none ${overridePresent && scheduleViewMode !== "workers" ? "mt-3" : ""}`}
        >
          {scheduleViewMode === "workers" ? (
            <ScopeBarWorkerInitialsRow
              workers={scopeBlock.worker_assignment ?? []}
              overridePresent={overridePresent}
              scopeBarWidth={displayWidth}
              scopeLabel={scopeBar.label}
              clippedStart={scopeBar.clippedStart}
              clippedEnd={scopeBar.clippedEnd}
              onExpandedChange={setWorkerInitialsExpanded}
            />
          ) : (
            <span className="min-w-0 flex-1 truncate">
              {scopeBar.clippedStart ? "…" : ""}
              {scopeBar.label}
              {scopeBar.clippedEnd ? "…" : ""}
            </span>
          )}
        </span>
        {!readOnly && scopeBar.resizeEnd && !scopeBar.clippedEnd ? (
          endResizeLocked ? (
            <div
              title={PLANNING_TOOLTIP_MESSAGES.draggingDisabledDueToLockedDay}
              aria-label={PLANNING_TOOLTIP_MESSAGES.draggingDisabledDueToLockedDay}
              className="absolute inset-y-0 right-0 z-30 w-2 shrink-0 rounded-r border-0 bg-black/10 opacity-60"
            />
          ) : (
            <button
              type="button"
              data-schedule-resize-handle
              aria-label="Resize block end date"
              className="absolute inset-y-0 right-0 z-20 w-2 shrink-0 cursor-ew-resize touch-none rounded-r border-0 bg-black/10 hover:bg-black/15"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => beginResize(e, scopeBar.blockId, "end")}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
              onPointerCancel={onResizePointerCancel}
            />
          )
        ) : null}
      </div>
      {!readOnly ? (
        <CascadeDropDown
          open={barContextOpen && Boolean(barContextAnchor)}
          onClose={closeBarContextMenu}
          anchorRect={barContextAnchor}
          items={scopeBlock ? barContextItems : []}
        />
      ) : null}
      {!readOnly ? popoverPortal : null}
    </>
  );
}
